<template>
  <el-select
    v-model="selectedUserId"
    placeholder="ユーザーを選択"
    filterable
    remote
    :remote-method="searchUsers"
    :loading="loading"
    style="width: 100%"
    @change="handleUserChange"
  >
    <el-option
      v-for="user in filteredUsers"
      :key="user.id"
      :label="formatUserLabel(user)"
      :value="user.id"
    >
      <div class="user-option">
        <div class="user-name">{{ formatUserLabel(user) }}</div>
      </div>
    </el-option>
  </el-select>
</template>

<script>
import { ref, watch, onMounted, inject } from 'vue'
import axios from 'axios'

export default {
  name: 'UserSearchSelect',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    excludeCurrentUser: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'userSelected'],
  setup(props, { emit }) {
    const auth = inject('Auth')
    const backendUrl = inject('BackendUrl')
    const allUsers = ref([])
    const filteredUsers = ref([])
    const selectedUserId = ref(props.modelValue)
    const loading = ref(false)
    const currentUserId = ref('')

    const formatUserLabel = (user) => {
      return user.name || user.email || user.id
    }

    // Load all users
    const loadUsers = async () => {
      loading.value = true
      try {
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const response = await axios.get(`${backendUrl}/v1/admin/users`, {
          headers: { Authorization: `Bearer ${idToken}` }
        })
        
        allUsers.value = response.data
        
        // Filter out current user if excludeCurrentUser is true
        if (props.excludeCurrentUser && currentUserId.value) {
          filteredUsers.value = allUsers.value.filter(user => user.id !== currentUserId.value)
        } else {
          filteredUsers.value = allUsers.value
        }
      } catch (error) {
        console.error('Failed to load users:', error)
        filteredUsers.value = []
      } finally {
        loading.value = false
      }
    }

    // Get current user ID
    const getCurrentUserId = async () => {
      try {
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const response = await axios.get(`${backendUrl}/v1/user`, {
          headers: { Authorization: `Bearer ${idToken}` }
        })
        currentUserId.value = response.data.id
      } catch (error) {
        console.error('Failed to get current user:', error)
      }
    }

    // Search users
    const searchUsers = (query) => {
      if (!query) {
        if (props.excludeCurrentUser && currentUserId.value) {
          filteredUsers.value = allUsers.value.filter(user => user.id !== currentUserId.value)
        } else {
          filteredUsers.value = allUsers.value
        }
        return
      }

      const searchQuery = query.toLowerCase()
      let results = allUsers.value.filter(user => {
        const nameMatch = user.name?.toLowerCase().includes(searchQuery)
        const emailMatch = user.email?.toLowerCase().includes(searchQuery)
        return nameMatch || emailMatch
      })

      // Filter out current user if excludeCurrentUser is true
      if (props.excludeCurrentUser && currentUserId.value) {
        results = results.filter(user => user.id !== currentUserId.value)
      }

      filteredUsers.value = results
    }

    // Handle user selection change
    const handleUserChange = (userId) => {
      emit('update:modelValue', userId)
      
      const selectedUser = allUsers.value.find(user => user.id === userId)
      if (selectedUser) {
        emit('userSelected', selectedUser)
      }
    }

    // Watch for prop changes
    watch(() => props.modelValue, (newValue) => {
      selectedUserId.value = newValue
    })

    onMounted(async () => {
      await getCurrentUserId()
      await loadUsers()
    })

    return {
      selectedUserId,
      filteredUsers,
      loading,
      formatUserLabel,
      searchUsers,
      handleUserChange
    }
  }
}
</script>

<style scoped>
.user-option {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.user-name {
  font-weight: 500;
  color: #303133;
  text-align: left;
}
</style>